import { useCallback, useEffect } from 'react';
import { useMap } from 'react-use';

import { EditorView } from '@lyearn/lygo-core';
import { getPreviewElements } from '@/modules/corporate/modules/channel/views/ChannelDetails/utils/getPreviewElements';
import { MentionEntityTypeEnum, MentionInputType, PostUrlInput } from '@/types/schema';

interface InputMentionsMap {
  [key: string]: MentionInputType;
}

interface InputUrlsMap {
  [url: string]: PostUrlInput;
}

const useInputPreviews = (textContent?: string | null) => {
  const [
    mentions,
    { set: setMention, get: getMention, setAll: setMentions, reset: resetMentions },
  ] = useMap<InputMentionsMap>();

  const [
    previewUrls,
    { setAll: setUrlPreviews, get: getUrlPreview, set: setPreviewUrl, reset: resetUrlPreviews },
  ] = useMap<InputUrlsMap>();

  const resetPreviews = useCallback(() => {
    resetMentions();
    resetUrlPreviews();
  }, [resetMentions, resetUrlPreviews]);

  useEffect(() => {
    if (textContent?.length === 0) {
      resetPreviews();
    }
  }, [resetPreviews, textContent?.length]);

  const onRemoveMentionPreview = useCallback(
    (mentionId: string, type: MentionEntityTypeEnum) => {
      setMention(mentionId, {
        previewEnabled: false,
        entityId: mentionId,
        entityType: type,
      });
    },
    [setMention],
  );

  const removeUrlPreview = useCallback(
    (url: string) => {
      setPreviewUrl(url, { url, previewEnabled: false });
    },
    [setPreviewUrl],
  );

  const updateInputMap = useCallback(
    (inputMap: InputMentionsMap, element: Element, entityType: MentionEntityTypeEnum) => {
      //@ts-ignore
      const mentionId = element.pmViewDesc?.node?.attrs.id;

      if (mentionId) {
        const addedMention = getMention(mentionId);
        if (addedMention) {
          inputMap[mentionId] = addedMention;
        } else {
          inputMap[mentionId] = {
            entityId: mentionId,
            entityType,
            previewEnabled: true,
          };
        }
      }
    },
    [getMention],
  );

  const updatePreviews = useCallback(
    (editorView: EditorView) => {
      const { contentElements, eventElements, urls, userElements } = getPreviewElements(
        editorView.dom,
      );

      const inputMentionsMap: InputMentionsMap = {};
      contentElements?.forEach((el) =>
        updateInputMap(inputMentionsMap, el, MentionEntityTypeEnum.Content),
      );
      eventElements?.forEach((el) =>
        updateInputMap(inputMentionsMap, el, MentionEntityTypeEnum.Event),
      );
      userElements?.forEach((el) =>
        updateInputMap(inputMentionsMap, el, MentionEntityTypeEnum.User),
      );
      setMentions(inputMentionsMap);

      const inputUrlsPreviewObj: InputUrlsMap = {};
      urls.forEach((url) => {
        const existingUrlPreview = getUrlPreview(url);
        if (existingUrlPreview) {
          inputUrlsPreviewObj[url] = existingUrlPreview;
        } else {
          inputUrlsPreviewObj[url] = {
            url,
            previewEnabled: true,
          };
        }
      });
      setUrlPreviews(inputUrlsPreviewObj);
    },
    [getUrlPreview, setMentions, setUrlPreviews, updateInputMap],
  );

  return {
    previewUrls,
    mentions,
    removeUrlPreview,
    onRemoveMentionPreview,
    updatePreviews,
  };
};

export default useInputPreviews;
